import type { ComponentType } from 'react';
import type { HTMLMotionProps } from 'framer-motion';
import type { ComponentSwitcherContextType } from './ComponentSwitcher.Context';
import type { Components, ComponentItem } from './types';

import React, { useRef, useState, useEffect, useMemo } from 'react';

import { objectKeys, getSiblingPrev, getSiblingNext } from '../../utils/object';

import { forwardedRef } from '../../utils/react';
import { ComponentSwitcherContext } from './ComponentSwitcher.Context';

export type ComponentSwitcherProps<C extends Components = Components> = {
  components: C;
  show?: keyof C;
  motionProps?: HTMLMotionProps<'div'>;
  onUpdate?: (
    item: Exclude<
      {
        [K in keyof C]-?: {
          name: K;
          component: Exclude<C[K], undefined>;
        };
      }[keyof C],
      undefined
    >,
  ) => void;
};

function ComponentSwitcherComponent<C extends Components>({
  components,
  show,

  onUpdate,
}: ComponentSwitcherProps<C>) {
  const uid = useRef(0);

  const keys = useMemo(() => objectKeys(components), [components]);
  const [current, set] = useState<keyof C>(() => show ?? keys[0]);
  const [store] = useState(new Map());

  const setCurrent = (name: keyof C) => {
    onUpdate?.({
      name,
      component: components[name],
    } as Parameters<NonNullable<typeof onUpdate>>[0]);
    set(name);
  };

  const contextValue = useMemo(() => {
    const prev = getSiblingPrev(components, current);
    const next = getSiblingNext(components, current);

    return {
      components,
      store,
      current: { name: current, component: components[current] } as ComponentItem,
      ...(prev && { prev: { name: prev[0] as string, component: prev[1] } }),
      ...(next && { next: { name: next[0] as string, component: next[1] } }),
      actions: {
        reset() {
          store.clear();
          setCurrent(show ?? keys[0]);
        },
        next() {
          const next = getSiblingNext(components, current);
          if (next) {
            setCurrent(next[0] as string);
          }
        },
        prev() {
          const prev = getSiblingPrev(components, current);
          if (prev) {
            setCurrent(prev[0] as string);
          }
        },
        navigate(name: string) {
          if (keys.includes(name)) {
            setCurrent(name as keyof C);
          }
        },
        sendUpdate() {
          onUpdate?.({
            name: `${String(current)}-${uid.current++}`,
            component: components[current],
          } as Parameters<NonNullable<typeof onUpdate>>[0]);
        },
      },
    } satisfies ComponentSwitcherContextType;
  }, [show, current, keys, store]);

  useEffect(() => {
    if (show && current !== show) {
      setCurrent(show as string);
    }
  }, [show]);

  const Component = useMemo(() => components[current] as ComponentType, [components, current]);

  return (
    <ComponentSwitcherContext.Provider value={contextValue}>
      <Component key={current as string} />
    </ComponentSwitcherContext.Provider>
  );
}

export const ComponentSwitcher = forwardedRef(
  ComponentSwitcherComponent,
  'ComponentSwitcher',
  true,
) as typeof ComponentSwitcherComponent;
